import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { Link } from 'react-router-dom';
import { api, type ApiSuccess } from '../../lib/api';

type TabId = 'auctions' | 'properties' | 'facility';

export function MyRequestsPage() {
  const [tab, setTab] = useState<TabId>('auctions');

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">📋 طلباتي</h1>
      <p className="text-ink-secondary mb-6">جميع طلباتك في مكان واحد — مزادات، عقارات، صيانة</p>

      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={tab === 'auctions'} onClick={() => setTab('auctions')}>📨 طلبات إقامة المزادات</Tab>
        <Tab active={tab === 'properties'} onClick={() => setTab('properties')}>🏘️ طلبات العقارات</Tab>
        <Tab active={tab === 'facility'} onClick={() => setTab('facility')}>🔧 طلبات الصيانة</Tab>
      </div>

      {tab === 'auctions' && <AuctionRequestsTab />}
      {tab === 'properties' && <PropertyRequestsTab />}
      {tab === 'facility' && <FacilityRequestsTab />}
    </div>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-ink-muted hover:text-navy-dark'
    }`}>{children}</button>
  );
}

function AuctionRequestsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-auction-requests'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; propertyTitle: string; status: string; createdAt: string; auctionId: string | null }>>>('/api/me/auction-requests'),
  });
  const list = data?.data ?? [];

  if (isLoading) return <Loading />;
  if (!list.length) return <Empty icon="📨" text="لم تطلب إقامة أي مزاد بعد" cta={<Link to="/request-auction"><Button variant="primary">طلب مزاد على عقاري</Button></Link>} />;

  return (
    <div className="space-y-3">
      {list.map((r) => (
        <Card key={r.id}>
          <CardBody>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge color={r.status === 'APPROVED' ? 'green' : r.status === 'REJECTED' ? 'red' : 'amber'}>
                    {{ PENDING: 'قيد المراجعة', APPROVED: 'موافقة', REJECTED: 'مرفوض', SCHEDULED: 'مجدول' }[r.status] ?? r.status}
                  </Badge>
                  <span className="text-xs text-ink-muted">{new Date(r.createdAt).toLocaleDateString('ar-SA')}</span>
                </div>
                <h3 className="font-bold">{r.propertyTitle}</h3>
              </div>
              {r.auctionId && (
                <Link to={`/auctions/${r.auctionId}`}>
                  <Button size="sm" variant="primary">عرض المزاد</Button>
                </Link>
              )}
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function PropertyRequestsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-property-requests'],
    queryFn: () => api.get<ApiSuccess<Array<{
      id: string;
      kind: 'SEARCH' | 'OFFER';
      propertyType: string | null;
      city: string | null;
      district: string | null;
      minPrice: number | string | null;
      maxPrice: number | string | null;
      budgetLabel: string | null;
      status: 'NEW' | 'IN_PROGRESS' | 'MATCHED' | 'CLOSED';
      priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
      notes: string | null;
      createdAt: string;
      updatedAt: string;
      assignedBroker?: { id: string; name: string; phone?: string | null } | null;
    }>>>('/api/me/property-requests'),
  });
  const list = data?.data ?? [];

  if (isLoading) return <Loading />;
  if (!list.length) {
    return <Empty
      icon="🏘️"
      text="لم تقدّم أي طلب عقاري بعد"
      cta={<Link to="/contact"><Button variant="primary">📋 قدّم طلباً جديداً</Button></Link>}
    />;
  }

  return (
    <div className="space-y-3">
      {list.map((r) => (
        <Card key={r.id}>
          <CardBody>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Badge color={r.kind === 'OFFER' ? 'gold' : 'navy'}>
                    {r.kind === 'OFFER' ? '🏠 عرض عقار' : '🔍 بحث عن عقار'}
                  </Badge>
                  <span className="text-[10px] text-ink-muted">#{r.id.slice(-8).toUpperCase()}</span>
                  {r.priority === 'URGENT' && <Badge color="red">🔥 عاجل</Badge>}
                  {r.priority === 'HIGH' && <Badge color="amber">⬆️ مرتفع</Badge>}
                </div>
                <h3 className="font-bold text-navy-dark">
                  {[r.propertyType, r.city, r.district].filter(Boolean).join(' · ') || 'بحث عام'}
                </h3>
                <p className="text-xs text-ink-muted mt-1">
                  {r.budgetLabel ?? formatBudgetText(r.minPrice, r.maxPrice)}
                  {' · '}
                  📅 {new Date(r.createdAt).toLocaleDateString('ar-SA')}
                </p>
              </div>
              {r.assignedBroker && (
                <div className="bg-navy-soft rounded-lg px-3 py-2 text-xs">
                  <div className="text-[10px] text-ink-muted">المسوّق المسؤول</div>
                  <div className="font-bold text-navy-dark">{r.assignedBroker.name}</div>
                  {r.assignedBroker.phone && (
                    <a href={`tel:${r.assignedBroker.phone}`} className="text-[10px] text-navy hover:underline" dir="ltr">
                      📞 {r.assignedBroker.phone}
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* 🛤️ Timeline */}
            <RequestStatusTimeline status={r.status} />
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function formatBudgetText(min: number | string | null, max: number | string | null): string {
  const a = min ? Number(min) : null;
  const b = max ? Number(max) : null;
  if (a && b) return `الميزانية: ${a.toLocaleString('en-US')} - ${b.toLocaleString('en-US')} ر.س`;
  if (b) return `الميزانية: حتى ${b.toLocaleString('en-US')} ر.س`;
  if (a) return `الميزانية: من ${a.toLocaleString('en-US')} ر.س`;
  return 'بدون ميزانية محددة';
}

// 🛤️ مكوّن timeline لحالة الطلب
function RequestStatusTimeline({ status }: { status: 'NEW' | 'IN_PROGRESS' | 'MATCHED' | 'CLOSED' }) {
  const steps = [
    { id: 'NEW',         label: 'استُلم',       icon: '📩' },
    { id: 'IN_PROGRESS', label: 'قيد المعالجة',  icon: '⚙️' },
    { id: 'MATCHED',     label: 'تم العثور',     icon: '✓' },
    { id: 'CLOSED',      label: 'مكتمل',         icon: '🎉' },
  ];
  const currentIdx = steps.findIndex((s) => s.id === status);

  return (
    <div className="border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between gap-1">
        {steps.map((s, i) => {
          const passed = i <= currentIdx;
          const active = i === currentIdx;
          return (
            <div key={s.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center gap-1 shrink-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition border-2 ${
                  active ? 'bg-gold text-white border-gold shadow-md scale-110' :
                  passed ? 'bg-navy text-white border-navy' :
                  'bg-gray-100 text-gray-400 border-gray-200'
                }`}>
                  {s.icon}
                </div>
                <span className={`text-[10px] font-bold whitespace-nowrap ${
                  active ? 'text-gold' : passed ? 'text-navy-dark' : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 transition ${
                  i < currentIdx ? 'bg-navy' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FacilityRequestsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-facility-requests'],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; title: string; category: string; priority: string; status: string; cost: number | string; createdAt: string }>>>('/api/facility-requests/me').catch(() => ({ data: [] as Array<{ id: string; title: string; category: string; priority: string; status: string; cost: number | string; createdAt: string }> })),
  });
  const list = data?.data ?? [];

  if (isLoading) return <Loading />;
  if (!list.length) return <Empty icon="🔧" text="لم تطلب أي صيانة بعد" />;

  return (
    <div className="space-y-3">
      {list.map((r) => (
        <Card key={r.id}>
          <CardBody>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge color={r.status === 'COMPLETED' ? 'green' : r.status === 'CANCELLED' ? 'red' : 'amber'}>{r.status}</Badge>
                  <Badge color="navy">{r.category}</Badge>
                  <Badge color={r.priority === 'URGENT' ? 'red' : 'amber'}>{r.priority}</Badge>
                </div>
                <h3 className="font-bold">{r.title}</h3>
                <p className="text-xs text-ink-muted">{new Date(r.createdAt).toLocaleDateString('ar-SA')}</p>
              </div>
              {Number(r.cost) > 0 && (
                <span className="font-bold text-navy-dark">{Number(r.cost).toLocaleString('en-US')} ر.س</span>
              )}
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function Loading() { return <div className="text-center py-12 text-ink-muted">⟳ جاري التحميل…</div>; }
function Empty({ icon, text, cta }: { icon: string; text: string; cta?: React.ReactNode }) {
  return (
    <Card><CardBody>
      <div className="text-center py-16">
        <div className="text-6xl mb-3">{icon}</div>
        <p className="text-ink-muted mb-4">{text}</p>
        {cta}
      </div>
    </CardBody></Card>
  );
}
